import {
  annotation as csAnnotation,
  utilities as csToolUtils,
  cursors as csCursors,
  drawing as csDrawing,
  Enums as ToolEnums,
  state as csToolsState,
} from '@cornerstonejs/tools';
import {
  getEnabledElement,
  metaData,
  eventTarget,
  triggerEvent,
} from '@cornerstonejs/core';
import type { Types as CoreTypes } from '@cornerstonejs/core';
import {
  SPECTRAL_DOPPLER_OUT_OF_REGION,
  SPECTRAL_DOPPLER_TOOL_NAMES,
} from '@/models/spectralDoppler';
import { ANNOTATION_STYLE } from '@/theme/constants';
import {
  resolveSpectralRegion,
  buildCalibrationFromRegion,
} from './calibrationRegion';
import {
  deriveVTIValues,
  isPointInRegionBbox,
  clampToRegionBbox,
} from './calibrationMath';
import {
  formatDopplerTextLines,
  markSpectralAnnotationData,
  SpectralDopplerBaseTool,
  type UltrasoundRegionsMeta,
  type SpectralDopplerAnnotationData,
} from './SpectralDopplerBaseTool';
import {
  worldToPixel,
  clampWorldToRegionBbox,
  clampWorldToBaselineSide,
  snapWorldToBaseline,
} from './worldPixelCoords';
import {
  distanceToSegment,
  polylineSignature,
  simplifyPolylineDouglasPeucker,
} from './geometry';
import { applySmartLabelAnchor } from '../labelPlacement';

const {
  state: annotationStateApi,
  locking: annotationLocking,
  visibility: annotationVisibility,
} = csAnnotation;
const { viewportFilters } = csToolUtils;
const { elementCursor } = csCursors;

interface VTIAnnotationData extends SpectralDopplerAnnotationData {
  /** Signature of the envelope as hydrated; absent on reader-drawn traces. */
  aiPolylineSig?: string;
  handles: {
    points: CoreTypes.Point3[];
    activeHandleIndex: number | null;
    textBox?: unknown;
  };
  polyline?: CoreTypes.Point3[];
  cachedStats: Record<string, unknown>;
  label: string;
  /** Hydrated AI envelope — spectralValues are authoritative, never re-derived. */
  isAI?: boolean;
}

interface VTIAnnotation {
  annotationUID: string;
  highlighted: boolean;
  invalidated: boolean;
  isLocked: boolean;
  isVisible: boolean;
  metadata: { toolName: string };
  data: VTIAnnotationData;
}

function defaultGetTextLines(data: VTIAnnotationData): string[] {
  // Hydrated AI envelopes set data.label ("AI: E"/"AI: A"); manual traces
  // leave it empty (no-op).
  const labelLines = data.label ? [data.label] : [];
  if (!data.spectralValues) return labelLines;
  return [...labelLines, ...formatDopplerTextLines('vti', data.spectralValues)];
}

/**
 * Resolve which half of the baseline a finalized trace lives in. Endpoints
 * are snapped to Y0 at finalize, so `polyline[0]` is no longer a reliable
 * indicator — the point with the largest pixel-Y distance from Y0 (the peak
 * or trough of the envelope) is.
 */
function derivePolylineBaselineSide(
  viewport: CoreTypes.IViewport,
  polyline: CoreTypes.Point3[],
  calibration: { referencePixelY0: number }
): 'positive' | 'negative' {
  let bestPy = polyline.length ? worldToPixel(viewport, polyline[0])[1] : 0;
  let bestDist = Math.abs(bestPy - calibration.referencePixelY0);
  for (let i = 1; i < polyline.length; i += 1) {
    const [, py] = worldToPixel(viewport, polyline[i]);
    const d = Math.abs(py - calibration.referencePixelY0);
    if (d > bestDist) {
      bestDist = d;
      bestPy = py;
    }
  }
  return bestPy <= calibration.referencePixelY0 ? 'positive' : 'negative';
}

/** Minimum canvas-pixel distance between successive trace samples. */
const TRACE_SAMPLE_MIN_DISTANCE = 2;

/**
 * DP simplification tolerance (canvas-pixel) applied at finalize. The trace is
 * sampled at 2px during drag; without simplification a typical envelope yields
 * hundreds of handles. ~3px keeps shape fidelity well below image resolution.
 */
const TRACE_SIMPLIFY_TOLERANCE = 3;

class VTITraceTool extends SpectralDopplerBaseTool {
  static toolName = SPECTRAL_DOPPLER_TOOL_NAMES.VTI;

  constructor(
    toolProps: Record<string, unknown> = {},
    defaultToolProps: Record<string, unknown> = {
      supportedInteractionTypes: ['Mouse', 'Touch'],
      configuration: {
        getTextLines: defaultGetTextLines,
      },
    }
  ) {
    super(toolProps, defaultToolProps);

    const readCalibration = (
      viewport: CoreTypes.IViewport,
      at?: [number, number]
    ): ReturnType<typeof buildCalibrationFromRegion> | null => {
      const imageId =
        (
          viewport as unknown as {
            getCurrentImageId?: () => string | undefined;
          }
        ).getCurrentImageId?.() ?? '';
      const regionsMeta = metaData.get('ultrasoundRegions', imageId) as
        | UltrasoundRegionsMeta
        | undefined;
      const { region } = resolveSpectralRegion(regionsMeta?.regions, at);
      if (!region) return null;
      return buildCalibrationFromRegion(region);
    };

    this.addNewAnnotation = (evt: {
      detail: {
        element: HTMLDivElement;
        currentPoints: { world: CoreTypes.Point3; canvas: CoreTypes.Point2 };
      };
    }): VTIAnnotation | undefined => {
      const { element, currentPoints } = evt.detail;
      const worldPos = currentPoints.world;

      const enabled = getEnabledElement(element);
      if (!enabled) return undefined;
      const { viewport } = enabled;

      const [px, py] = worldToPixel(viewport, worldPos);
      const calibration = readCalibration(viewport, [px, py]);
      if (!calibration) return undefined;

      if (!isPointInRegionBbox(px, py, calibration)) {
        triggerEvent(eventTarget, SPECTRAL_DOPPLER_OUT_OF_REGION, {
          viewportId: viewport.id,
          px,
          py,
        });
        return undefined;
      }

      elementCursor.hideElementCursor(element);
      this.isDrawing = true;

      const annotation = (
        this as unknown as {
          createAnnotation: (
            evt: unknown,
            points: CoreTypes.Point3[]
          ) => VTIAnnotation;
        }
      ).createAnnotation(evt, [[...worldPos] as CoreTypes.Point3]);

      annotation.data.spectralCalibration = calibration;
      annotation.data.spectralToolVariant = 'vti';
      annotation.data.polyline = [[...worldPos] as CoreTypes.Point3];
      annotation.data.handles.activeHandleIndex = null;

      annotationStateApi.addAnnotation(annotation as unknown as never, element);

      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );

      // Lock the trace to whichever half of the baseline the user started on.
      // Pixel-Y grows downward, so above-baseline = pixel-Y ≤ Y0. We snap the
      // endpoints to Y0 only at finalize (see _endCallback), so during drag
      // the user keeps drawing where their cursor is.
      const baselineSide: 'positive' | 'negative' =
        py <= calibration.referencePixelY0 ? 'positive' : 'negative';

      this.editData = {
        annotation,
        viewportIdsToRender,
        newAnnotation: true,
        hasMoved: false,
        baselineSide,
      };

      this._activateDraw(element);
      (evt as { preventDefault?: () => void }).preventDefault?.();
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      return annotation;
    };

    this._dragCallback = (evt: {
      detail: {
        element: HTMLDivElement;
        currentPoints: { world: CoreTypes.Point3; canvas: CoreTypes.Point2 };
      };
    }) => {
      if (!this.editData) return;
      const { annotation } = this.editData;
      const ann = annotation as VTIAnnotation;
      const { world } = evt.detail.currentPoints;
      const enabled = getEnabledElement(evt.detail.element);
      const cal = ann.data.spectralCalibration;

      // Redraw mode: capture the drag path. We don't mutate the polyline
      // until release — _endCallback splices the captured path in. During
      // drag, renderAnnotation overlays the captured path so the user sees
      // exactly what they're drawing.
      if (this.editData.isRedraw && !this.editData.newAnnotation) {
        if (this.editData.movingTextBox) return;
        const { canvas } = evt.detail.currentPoints;
        const captured = this.editData.redrawCanvasPoints as [number, number][];
        const last = captured[captured.length - 1];
        if (last) {
          const dx = canvas[0] - last[0];
          const dy = canvas[1] - last[1];
          if (Math.hypot(dx, dy) < TRACE_SAMPLE_MIN_DISTANCE) return;
        }
        captured.push([canvas[0], canvas[1]]);
        this.editData.hasMoved = true;
        // Mark invalidated so renderAnnotation re-runs and re-derives VTI
        // off the (live-fused) polyline.
        ann.invalidated = true;
        csToolUtils.triggerAnnotationRenderForViewportIds(
          this.editData.viewportIdsToRender
        );
        return;
      }

      // Edit mode: a finalized annotation's vertex (endpoint or just-inserted)
      // is being dragged.
      if (
        this.editData.polylineIndex !== undefined &&
        !this.editData.newAnnotation
      ) {
        const idx = this.editData.polylineIndex as number;
        if (this.editData.movingTextBox) return;

        let clampedWorld: CoreTypes.Point3;
        if (enabled && cal) {
          const bboxClamped = clampWorldToRegionBbox(
            enabled.viewport,
            world,
            cal
          );
          // Derive the locked side from the polyline point farthest from Y0
          // — endpoints are snapped to baseline at finalize so polyline[0] is
          // no longer a reliable side indicator; the peak/trough is.
          let side = this.editData.baselineSide as
            | 'positive'
            | 'negative'
            | undefined;
          if (!side && ann.data.polyline?.length) {
            side = derivePolylineBaselineSide(
              enabled.viewport,
              ann.data.polyline,
              cal
            );
            this.editData.baselineSide = side;
          }
          clampedWorld = side
            ? clampWorldToBaselineSide(enabled.viewport, bboxClamped, cal, side)
            : bboxClamped;
        } else {
          clampedWorld = [...world] as CoreTypes.Point3;
        }

        // Polyline is the source of truth for the VTI calculus.
        if (ann.data.polyline) {
          ann.data.polyline[idx] = clampedWorld;
        }
        // Keep handles.points (which tracks the rendered endpoints) in sync
        // when the dragged vertex is one of them.
        const lastIdx = (ann.data.polyline?.length ?? 0) - 1;
        if (idx === 0 && ann.data.handles.points.length >= 1) {
          ann.data.handles.points[0] = clampedWorld;
        } else if (idx === lastIdx && ann.data.handles.points.length >= 2) {
          ann.data.handles.points[1] = clampedWorld;
        }
        ann.invalidated = true;
        this.editData.hasMoved = true;
        csToolUtils.triggerAnnotationRenderForViewportIds(
          this.editData.viewportIdsToRender
        );
        return;
      }

      // TextBox drag (post-creation): mark the textbox as user-moved so the
      // smart-label auto-placement no longer overrides it.
      if (this.editData.movingTextBox) {
        const delta = (
          evt.detail as {
            deltaPoints?: { world?: CoreTypes.Point3 };
          }
        ).deltaPoints?.world;
        const tb = (
          ann.data.handles as {
            textBox?: {
              worldPosition?: number[];
              userMoved?: boolean;
            };
          }
        ).textBox;
        if (delta && tb && tb.worldPosition) {
          tb.worldPosition[0] += delta[0];
          tb.worldPosition[1] += delta[1];
          tb.worldPosition[2] += delta[2];
          tb.userMoved = true;
        }
        this.editData.hasMoved = true;
        csToolUtils.triggerAnnotationRenderForViewportIds(
          this.editData.viewportIdsToRender
        );
        return;
      }

      // Draw mode: append to the polyline.
      if (!this.isDrawing) return;
      const polyline = ann.data.polyline ?? [];
      const { canvas } = evt.detail.currentPoints;

      // Only append if the canvas-space movement exceeds our threshold.
      const last = polyline[polyline.length - 1];
      if (last && enabled) {
        const lastCanvas = enabled.viewport.worldToCanvas(last);
        const dx = canvas[0] - lastCanvas[0];
        const dy = canvas[1] - lastCanvas[1];
        if (Math.hypot(dx, dy) < TRACE_SAMPLE_MIN_DISTANCE) return;
      }

      let clampedWorld: CoreTypes.Point3;
      if (enabled && cal) {
        const bboxClamped = clampWorldToRegionBbox(
          enabled.viewport,
          world,
          cal
        );
        const side = this.editData.baselineSide as
          | 'positive'
          | 'negative'
          | undefined;
        clampedWorld = side
          ? clampWorldToBaselineSide(enabled.viewport, bboxClamped, cal, side)
          : bboxClamped;
      } else {
        clampedWorld = [...world] as CoreTypes.Point3;
      }
      polyline.push(clampedWorld);
      ann.data.polyline = polyline;
      // Keep the last handle in sync with the most recent point so the head
      // of the trace shows a handle dot during drag.
      ann.data.handles.points = [polyline[0], polyline[polyline.length - 1]];
      ann.invalidated = true;
      this.editData.hasMoved = true;
      csToolUtils.triggerAnnotationRenderForViewportIds(
        this.editData.viewportIdsToRender
      );
    };

    this._endCallback = (evt: { detail: { element: HTMLDivElement } }) => {
      const { element } = evt.detail;
      if (!this.editData) return;

      const { annotation, viewportIdsToRender, newAnnotation, hasMoved } =
        this.editData;
      const ann = annotation as VTIAnnotation;

      // Require a real drag — a single click produces no meaningful VTI.
      if (
        newAnnotation &&
        (!hasMoved || (ann.data.polyline?.length ?? 0) < 2)
      ) {
        const uid = ann.annotationUID;
        if (uid) annotationStateApi.removeAnnotation(uid);
        this._deactivateDraw(element);
        this._deactivateModify(element);
        elementCursor.resetElementCursor(element);
        this.editData = null;
        this.isDrawing = false;
        return;
      }

      // Redraw finalization: splice the captured drag path into the polyline,
      // replacing the section between the segments closest to the click and
      // release points. Same baseline-side + bbox clamps as draw mode.
      const enabledForSplice = getEnabledElement(element);
      if (
        !newAnnotation &&
        this.editData.isRedraw &&
        hasMoved &&
        enabledForSplice &&
        ann.data.polyline &&
        ann.data.polyline.length >= 2
      ) {
        const { viewport: vp } = enabledForSplice;
        const cal = ann.data.spectralCalibration;
        const captured = this.editData.redrawCanvasPoints as [number, number][];
        const startSegIdx = this.editData.redrawStartSegIdx as number;
        const baselineSide = this.editData.baselineSide as
          | 'positive'
          | 'negative'
          | undefined;

        // Find the segment closest to the release point.
        const releaseCanvas = captured[captured.length - 1];
        let endSegIdx = ann.data.polyline.length - 2;
        let minDist = Infinity;
        for (let j = 1; j < ann.data.polyline.length; j += 1) {
          const a = vp.worldToCanvas(ann.data.polyline[j - 1]);
          const b = vp.worldToCanvas(ann.data.polyline[j]);
          const d = distanceToSegment(a, b, releaseCanvas);
          if (d < minDist) {
            minDist = d;
            endSegIdx = j - 1;
          }
        }

        // DP-simplify the captured drag the same way creation does.
        const indexedCaptured = captured.map(
          (c, i) => [c[0], c[1], i] as [number, number, number]
        );
        const simplifiedCaptured =
          captured.length > 2
            ? simplifyPolylineDouglasPeucker(
                indexedCaptured,
                TRACE_SIMPLIFY_TOLERANCE
              ).map((p) => [p[0], p[1]] as [number, number])
            : captured.map((c) => [c[0], c[1]] as [number, number]);

        // Convert captured canvas points to world, with the same clamps the
        // draw path uses so the redraw can't escape the region or cross the
        // baseline.
        const capturedWorld: CoreTypes.Point3[] = simplifiedCaptured.map(
          (c) => {
            const w = vp.canvasToWorld([c[0], c[1]] as CoreTypes.Point2);
            if (cal) {
              const bboxClamped = clampWorldToRegionBbox(vp, w, cal);
              return baselineSide
                ? clampWorldToBaselineSide(vp, bboxClamped, cal, baselineSide)
                : bboxClamped;
            }
            return [...w] as CoreTypes.Point3;
          }
        );

        // If the user redrew "backwards" (release segment earlier than click
        // segment), reverse the captured path so it flows the same direction
        // as the original polyline.
        let lo = startSegIdx;
        let hi = endSegIdx;
        let pathToInsert = capturedWorld;
        if (lo > hi) {
          [lo, hi] = [hi, lo];
          pathToInsert = [...capturedWorld].reverse();
        }

        // Splice: keep vertices [0..lo] inclusive, insert captured path,
        // keep vertices [hi+1..end].
        ann.data.polyline = [
          ...ann.data.polyline.slice(0, lo + 1),
          ...pathToInsert,
          ...ann.data.polyline.slice(hi + 1),
        ];
        ann.invalidated = true;
      }

      const enabled = getEnabledElement(element);
      const viewport = enabled?.viewport;

      // VTI envelopes start and end on the baseline by definition. Snapping
      // happens at finalize (not during drag) so the user keeps free movement
      // while drawing — the trace closes onto Y0 only on release. Redraws
      // don't reach this branch and preserve endpoints by construction.
      if (
        newAnnotation &&
        viewport &&
        ann.data.polyline &&
        ann.data.polyline.length >= 2
      ) {
        const cal = ann.data.spectralCalibration;
        if (cal) {
          const lastIdx = ann.data.polyline.length - 1;
          ann.data.polyline[0] = snapWorldToBaseline(
            viewport,
            ann.data.polyline[0],
            cal
          );
          ann.data.polyline[lastIdx] = snapWorldToBaseline(
            viewport,
            ann.data.polyline[lastIdx],
            cal
          );
        }
      }

      // On creation: simplify the dense drag-sampled polyline into a manageable
      // set of handles (Douglas–Peucker in canvas space). The simplified
      // polyline becomes the source of truth — every kept point is a handle.
      if (
        newAnnotation &&
        viewport &&
        ann.data.polyline &&
        ann.data.polyline.length > 2
      ) {
        const canvasPts = ann.data.polyline.map((w) => {
          const c = viewport.worldToCanvas(w);
          // Stash original world index for fast re-lookup.
          return [c[0], c[1]] as [number, number];
        });
        const indexedCanvas = canvasPts.map(
          (c, i) => [c[0], c[1], i] as [number, number, number]
        );
        const simplified = simplifyPolylineDouglasPeucker(
          indexedCanvas,
          TRACE_SIMPLIFY_TOLERANCE
        );
        const keptIndices = simplified.map((p) => p[2]);
        ann.data.polyline = keptIndices.map(
          (i) => (ann.data.polyline as CoreTypes.Point3[])[i]
        );
      }

      // Keep only the endpoints in handles.points. Middle clicks should fall
      // through to toolSelectedCallback (which inserts a new vertex at the
      // click and lets the user drag it), not get caught by handle hit-test.
      if (ann.data.polyline && ann.data.polyline.length >= 2) {
        ann.data.handles.points = [
          [...ann.data.polyline[0]] as CoreTypes.Point3,
          [
            ...ann.data.polyline[ann.data.polyline.length - 1],
          ] as CoreTypes.Point3,
        ];
      }
      ann.data.handles.activeHandleIndex = null;

      // Finalize: compute VTI in pixel space (uses simplified polyline).
      if (viewport && ann.data.polyline) {
        const cal = ann.data.spectralCalibration ?? readCalibration(viewport);
        if (cal) {
          const pixelPts: [number, number][] = ann.data.polyline.map((w) => {
            const [px, py] = worldToPixel(viewport, w);
            return clampToRegionBbox(px, py, cal);
          });
          const values = deriveVTIValues(pixelPts, cal);
          markSpectralAnnotationData(
            ann.data as unknown as Record<string, unknown>,
            'vti',
            cal,
            values,
            pixelPts
          );
        }
      }

      this._deactivateDraw(element);
      this._deactivateModify(element);
      elementCursor.resetElementCursor(element);

      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      if (newAnnotation) {
        annotationStateApi.triggerAnnotationCompleted(
          annotation as unknown as never
        );
      }

      this.editData = null;
      this.isDrawing = false;
    };

    this.cancel = (element: HTMLDivElement): string | undefined => {
      if (!this.editData) return undefined;
      const { annotation, newAnnotation } = this.editData;
      this.isDrawing = false;
      this._deactivateDraw(element);
      this._deactivateModify(element);
      elementCursor.resetElementCursor(element);
      const uid = (annotation as VTIAnnotation).annotationUID;
      // Only remove the annotation if cancellation happened mid-draw —
      // cancel during edit should leave the existing annotation alone.
      if (uid && newAnnotation) annotationStateApi.removeAnnotation(uid);
      this.editData = null;
      return uid;
    };

    this._activateDraw = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = true;
      element.addEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
    };

    this._deactivateDraw = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = false;
      element.removeEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
    };

    this._activateModify = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = true;
      element.addEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.addEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this._deactivateModify = (element: HTMLDivElement) => {
      csToolsState.isInteractingWithTool = false;
      element.removeEventListener(
        ToolEnums.Events.MOUSE_UP,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.MOUSE_CLICK,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_END,
        this._endCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_DRAG,
        this._dragCallback as EventListener
      );
      element.removeEventListener(
        ToolEnums.Events.TOUCH_TAP,
        this._endCallback as EventListener
      );
    };

    this.handleSelectedCallback = (
      evt: { detail: { element: HTMLDivElement } },
      annotation: VTIAnnotation,
      handle: unknown
    ) => {
      const { element } = evt.detail;
      // eslint-disable-next-line no-param-reassign
      annotation.highlighted = true;

      // Cornerstone passes either an endpoint handle (a Point3 reference from
      // handles.points) or the textBox handle (object with worldPosition).
      let movingTextBox = false;
      let polylineIndex: number | undefined;
      const h = handle as { worldPosition?: unknown };
      if (h && h.worldPosition) {
        movingTextBox = true;
      } else {
        // handles.points is [first, last] post-finalize, so map the click to
        // the matching polyline index (0 or polyline.length - 1).
        const handleIdx = annotation.data.handles.points.findIndex(
          (p) => p === handle
        );
        const poly = annotation.data.polyline;
        if (handleIdx === 0) {
          polylineIndex = 0;
        } else if (handleIdx === 1 && poly && poly.length > 0) {
          polylineIndex = poly.length - 1;
        }
      }

      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );
      this.editData = {
        annotation,
        viewportIdsToRender,
        polylineIndex,
        movingTextBox,
        newAnnotation: false,
        hasMoved: false,
      };
      this._activateModify(element);
      elementCursor.hideElementCursor(element);
      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      (evt as unknown as { preventDefault?: () => void }).preventDefault?.();
    };

    this.toolSelectedCallback = (
      evt: {
        detail: {
          element: HTMLDivElement;
          currentPoints?: {
            world: CoreTypes.Point3;
            canvas: CoreTypes.Point2;
          };
        };
      },
      annotation: VTIAnnotation
    ) => {
      const { element, currentPoints } = evt.detail;
      // eslint-disable-next-line no-param-reassign
      annotation.highlighted = true;
      const viewportIdsToRender =
        viewportFilters.getViewportIdsWithToolToRender(
          element,
          this.getToolName()
        );

      // Edit-by-redraw (FreehandROI-style): clicking on the line and dragging
      // captures a new path; on release we splice that path into the polyline
      // between the segments closest to the click and release points. The
      // VTI pipeline is unchanged — only the polyline is rewritten.
      const enabled = currentPoints ? getEnabledElement(element) : undefined;
      const poly = annotation.data.polyline;
      if (enabled && currentPoints && poly && poly.length >= 2) {
        const { viewport } = enabled;
        const { canvas: clickCanvas } = currentPoints;
        const cal = annotation.data.spectralCalibration;

        // Find the polyline segment closest to the click; remember the
        // baseline side so the redraw is constrained the same way as draw.
        let startSegIdx = 0;
        let minDist = Infinity;
        for (let j = 1; j < poly.length; j += 1) {
          const a = viewport.worldToCanvas(poly[j - 1]);
          const b = viewport.worldToCanvas(poly[j]);
          const d = distanceToSegment(a, b, clickCanvas);
          if (d < minDist) {
            minDist = d;
            startSegIdx = j - 1;
          }
        }

        // Side is derived from the polyline point farthest from Y0 because
        // the endpoints are snapped to baseline at finalize.
        const baselineSide: 'positive' | 'negative' | undefined = cal
          ? derivePolylineBaselineSide(viewport, poly, cal)
          : undefined;

        this.editData = {
          annotation,
          viewportIdsToRender,
          isRedraw: true,
          redrawStartSegIdx: startSegIdx,
          redrawCanvasPoints: [[clickCanvas[0], clickCanvas[1]]],
          movingTextBox: false,
          newAnnotation: false,
          hasMoved: false,
          baselineSide,
        };
        this._activateModify(element);
        elementCursor.hideElementCursor(element);
      }

      csToolUtils.triggerAnnotationRenderForViewportIds(viewportIdsToRender);
      (evt as unknown as { preventDefault?: () => void }).preventDefault?.();
    };

    this.isPointNearTool = (
      element: HTMLDivElement,
      annotation: VTIAnnotation,
      canvasCoords: CoreTypes.Point2,
      proximity: number
    ): boolean => {
      const { polyline } = annotation.data;
      if (!polyline || polyline.length < 2) return false;
      const enabled = getEnabledElement(element);
      if (!enabled) return false;
      const { viewport } = enabled;
      for (let i = 1; i < polyline.length; i += 1) {
        const a = viewport.worldToCanvas(polyline[i - 1]);
        const b = viewport.worldToCanvas(polyline[i]);
        if (distanceToSegment(a, b, canvasCoords) <= proximity) return true;
      }
      return false;
    };

    this.renderAnnotation = (
      enabledElement: CoreTypes.IEnabledElement,
      svgDrawingHelper: unknown
    ): boolean => {
      let rendered = false;
      const { viewport } = enabledElement;
      const { element } = viewport;

      let annotations = annotationStateApi.getAnnotations(
        this.getToolName(),
        element
      ) as unknown as VTIAnnotation[] | undefined;
      if (!annotations?.length) return rendered;
      annotations = this.filterInteractableAnnotationsForElement(
        element,
        annotations as unknown as Record<string, unknown>[]
      ) as unknown as VTIAnnotation[];
      if (!annotations?.length) return rendered;

      const styleSpecifier = {
        toolGroupId: this.toolGroupId,
        toolName: this.getToolName(),
        viewportId: viewport.id,
      };

      for (let i = 0; i < annotations.length; i += 1) {
        const annotation = annotations[i];
        const { annotationUID, data } = annotation;
        const { polyline } = data;
        if (
          polyline &&
          polyline.length >= 2 &&
          annotationVisibility.isAnnotationVisible(annotationUID)
        ) {
          // Re-derive on invalidation (handle drag finalized OR mid-drag).
          // The polyline is the source of truth; handles.points mirror it.
          // AI envelopes carry the backend's reported VTI; re-integrating the
          // rounded integer-pixel polyline would drift ~3% off the report, so
          // keep spectralValues as-is (AP-616).
          // An untouched AI envelope keeps the backend's reported VTI:
          // re-integrating the rounded integer-pixel polyline drifts ~3% off the
          // report (AP-616). Once a reader reshapes it that value describes a
          // trace that no longer exists, so re-derive — which also fires
          // StatsUpdated and lets useMeasurementCapture adopt it to manual.
          const aiEnvelopeEdited =
            !!data.isAI &&
            !!data.aiPolylineSig &&
            polylineSignature(polyline) !== data.aiPolylineSig;
          if (
            annotation.invalidated &&
            data.spectralCalibration &&
            (!data.isAI || aiEnvelopeEdited)
          ) {
            const cal = data.spectralCalibration;
            const pixelPts: [number, number][] = polyline.map((w) => {
              const [px, py] = worldToPixel(viewport, w);
              return clampToRegionBbox(px, py, cal);
            });
            data.spectralValues = deriveVTIValues(pixelPts, cal);
            (
              data as unknown as {
                spectralPixelPoints?: [number, number][];
              }
            ).spectralPixelPoints = pixelPts;
            annotation.invalidated = false;
            annotationStateApi.triggerAnnotationModified(
              annotation as unknown as never,
              element,
              ToolEnums.ChangeTypes.StatsUpdated
            );
          }

          const styleCtx = { ...styleSpecifier, annotationUID };
          const { color, lineWidth } = this.getAnnotationStyle({
            annotation,
            styleSpecifier: styleCtx,
          }) as { color: string; lineWidth: number };

          const canvasPolyline = polyline.map((w) => viewport.worldToCanvas(w));

          csDrawing.drawPolyline(
            svgDrawingHelper as never,
            annotationUID,
            `${annotationUID}-trace`,
            canvasPolyline,
            { color, width: lineWidth, closePath: false }
          );

          if (
            !annotationLocking.isAnnotationLocked(annotationUID) &&
            canvasPolyline.length >= 2
          ) {
            // Match FreehandROI: only the endpoints get visible handles. The
            // line itself is editable via toolSelectedCallback, which captures
            // a drag-redraw and splices it into the polyline on release.
            csDrawing.drawHandles(
              svgDrawingHelper as never,
              annotationUID,
              `${annotationUID}-handles`,
              [canvasPolyline[0], canvasPolyline[canvasPolyline.length - 1]],
              // Radius must be defined: getAnnotationStyle omits it, and
              // undefined wipes drawHandle's default. Colour carries selection.
              { color, handleRadius: ANNOTATION_STYLE.handleRadiusActive }
            );
          }

          // Redraw preview: while the user is dragging on this annotation,
          // overlay the captured drag path so they see exactly what will be
          // spliced in on release.
          const ed = this.editData as
            | {
                isRedraw?: boolean;
                annotation?: VTIAnnotation;
                redrawCanvasPoints?: [number, number][];
              }
            | null
            | undefined;
          if (
            ed?.isRedraw &&
            ed.annotation === annotation &&
            ed.redrawCanvasPoints &&
            ed.redrawCanvasPoints.length >= 2
          ) {
            csDrawing.drawPolyline(
              svgDrawingHelper as never,
              annotationUID,
              `${annotationUID}-redraw`,
              ed.redrawCanvasPoints as unknown as CoreTypes.Point2[],
              { color, width: lineWidth, closePath: false, lineDash: '4,3' }
            );
          }

          const getTextLines = this.configuration?.getTextLines as
            | ((data: VTIAnnotationData) => string[])
            | undefined;
          const textLines = getTextLines?.(data) ?? [];

          // Locked above the baseline where the trace first started.
          const startWorld = data.spectralCalibration
            ? snapWorldToBaseline(
                viewport,
                polyline[0],
                data.spectralCalibration
              )
            : polyline[0];
          applySmartLabelAnchor({
            annotation: annotation as unknown as {
              data: { handles: { textBox?: unknown } };
            },
            geometryCanvasPoints: canvasPolyline,
            // Room for its 5 lines: name, VTI, Peak, Mean, Δt.
            spot: {
              point: viewport.worldToCanvas(startWorld),
              side: 'above',
              maxLines: 5,
              lockedAt: startWorld,
            },
            lines: textLines,
            viewport: viewport as unknown as {
              element: HTMLElement;
              canvasToWorld: (p: CoreTypes.Point2) => CoreTypes.Point3;
            },
          });

          this.renderLinkedTextBoxAnnotation({
            enabledElement,
            svgDrawingHelper,
            annotation,
            styleSpecifier: styleCtx,
            textLines,
            canvasCoordinates: canvasPolyline,
          });

          rendered = true;

          const flagged = annotation as unknown as {
            _spectralStatsEmitted?: boolean;
          };
          // Bootstrap StatsUpdated exists so a reloaded HUMAN annotation
          // re-registers its stats with the capture layer. Never emit it for
          // AI annotations: capture treats an AI StatsUpdated as "reader took
          // ownership" and instantly adopts — a first RENDER must not convert
          // an AI measurement into a manual one (unsaved-changes on load).
          if (
            !flagged._spectralStatsEmitted &&
            data.spectralValues &&
            !data.isAI
          ) {
            flagged._spectralStatsEmitted = true;
            annotationStateApi.triggerAnnotationModified(
              annotation as unknown as never,
              element,
              ToolEnums.ChangeTypes.StatsUpdated
            );
          }
        }
      }

      return rendered;
    };
  }
}

export default VTITraceTool;
